import { useState } from 'react';
import { Phone, ChevronDown, ChevronUp, AlertTriangle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cur } from '@/lib/currency';

function fmt(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function borderColor(maxDays) {
  if (maxDays > 90) return 'border-red-400';
  if (maxDays > 60) return 'border-orange-400';
  if (maxDays > 30) return 'border-amber-400';
  return 'border-emerald-400';
}

function agingColor(days) {
  if (days > 90) return 'text-red-600 bg-red-50';
  if (days > 60) return 'text-orange-600 bg-orange-50';
  if (days > 30) return 'text-amber-600 bg-amber-50';
  return 'text-emerald-600 bg-emerald-50';
}

export default function OutstandingCustomerCard({ customer, onCollect }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const bc = borderColor(customer.max_overdue_days);
  // Defensive: backend might omit the invoices array
  const invoices = Array.isArray(customer.invoices) ? customer.invoices : [];

  return (
    <div className={`bg-card border-2 ${bc} rounded-2xl overflow-hidden transition-all`}>
      {/* Main row */}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-foreground text-base">{customer.name}</p>
              {customer.max_overdue_days > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${agingColor(customer.max_overdue_days)}`}>
                  {customer.max_overdue_days > 90 ? '🔴' : customer.max_overdue_days > 60 ? '🟠' : '🟡'} {customer.max_overdue_days} يوم متأخر
                </span>
              )}
            </div>
            {customer.phone && (
              <div className="flex items-center gap-1 mt-0.5">
                <Phone className="w-3 h-3 text-muted-foreground" />
                <a href={`tel:${customer.phone}`} className="text-xs text-muted-foreground">{customer.phone}</a>
              </div>
            )}
            {customer.city && <p className="text-xs text-muted-foreground">{customer.city}</p>}
          </div>
          <div className="text-left flex-shrink-0">
            <p className="text-xs text-muted-foreground">إجمالي المستحق</p>
            <p className="text-lg font-black text-foreground">{fmt(customer.total)} {cur()}</p>
          </div>
        </div>

        {/* Aging buckets */}
        <div className="grid grid-cols-3 gap-1.5 text-xs">
          {customer.b0 > 0 && (
            <div className="bg-emerald-50 rounded-lg p-1.5 text-center">
              <p className="text-emerald-700 font-bold">{fmt(customer.b0)}</p>
              <p className="text-emerald-600 text-[10px]">0-30 يوم</p>
            </div>
          )}
          {customer.b30 > 0 && (
            <div className="bg-amber-50 rounded-lg p-1.5 text-center">
              <p className="text-amber-700 font-bold">{fmt(customer.b30)}</p>
              <p className="text-amber-600 text-[10px]">31-60 يوم</p>
            </div>
          )}
          {customer.b60 > 0 && (
            <div className="bg-orange-50 rounded-lg p-1.5 text-center">
              <p className="text-orange-700 font-bold">{fmt(customer.b60)}</p>
              <p className="text-orange-600 text-[10px]">61-90 يوم</p>
            </div>
          )}
          {customer.b90plus > 0 && (
            <div className="bg-red-50 rounded-lg p-1.5 text-center col-span-1">
              <div className="flex items-center justify-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5 text-red-600" />
                <p className="text-red-700 font-bold">{fmt(customer.b90plus)}</p>
              </div>
              <p className="text-red-600 text-[10px]">+90 يوم</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {customer.phone && (
            <a href={`tel:${customer.phone}`}
              className="flex-1 bg-muted rounded-xl py-2 text-xs font-semibold text-center flex items-center justify-center gap-1 text-foreground hover:bg-muted/70 transition-colors">
              📞 اتصال
            </a>
          )}
          <button onClick={() => onCollect(customer)}
            className="flex-1 bg-secondary text-white rounded-xl py-2 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-secondary/90 transition-colors">
            💰 تحصيل
          </button>
          <button onClick={() => navigate(`/visits/new?customer_id=${customer.id}&customer_name=${encodeURIComponent(customer.name)}`)}
            className="flex-1 bg-primary/10 text-primary rounded-xl py-2 text-xs font-semibold flex items-center justify-center gap-1 hover:bg-primary/20 transition-colors">
            📍 زيارة
          </button>
        </div>
      </div>

      {/* Expand invoices */}
      {invoices.length > 0 && (
        <button onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center justify-center gap-1 py-2 bg-muted/30 text-xs text-muted-foreground hover:bg-muted/60 transition-colors border-t border-border">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'إخفاء' : `عرض الفواتير (${invoices.length})`}
        </button>
      )}

      {expanded && invoices.length > 0 && (
        <div className="divide-y divide-border">
          {invoices.map(inv => (
            <div key={inv.id} className="px-4 py-3 flex items-center gap-3">
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{inv.name}</p>
                <p className="text-xs text-muted-foreground">
                  استحقاق: {inv.invoice_date_due || '—'}
                  {inv.overdue_days > 0 && <span className="text-red-600 mr-1"> · متأخر {inv.overdue_days} يوم</span>}
                </p>
              </div>
              <p className="font-bold text-primary text-sm whitespace-nowrap">{fmt(inv.amount_residual)} {cur()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}